// @ts-nocheck
/**
 * 報酬計算システム
 *
 * 責務:
 * - フロア/ボスクリア時の経験値計算
 * - ゴールド報酬の計算
 * - 各種ボーナス適用
 */

/**
 * 基本報酬定数
 */
const BASE_REWARDS = {
  // フロアクリア基本報酬
  floor: {
    experience: 50,  // 基本経験値
    gold: 30,        // 基本ゴールド
  },
  // ボス撃破基本報酬
  boss: {
    experience: 500,
    gold: 300,
  },
};

/**
 * 深度ボーナス倍率（10階ごと）
 */
const DEPTH_MULTIPLIERS = {
  1: 1.0,   // 1-10階
  2: 1.3,   // 11-20階
  3: 1.6,   // 21-30階
  4: 2.0,   // 31-40階
  5: 2.5,   // 41-50階
  6: 3.0,   // 51-60階
  7: 3.5,   // 61-70階
  8: 4.0,   // 71-80階
  9: 4.5,   // 81-90階
  10: 5.0,  // 91-100階
};

/**
 * 大陸別ボーナス（continents.jsの定義に準拠）
 */
const CONTINENT_BONUSES = {
  aviculus: { experience: 1.05, gold: 1.05 },       // 巨鳥の島（炎・光）：バランス型
  black_curse: { experience: 1.15, gold: 0.95 },    // 黒呪の大陸（闇・水）：経験値+15%
  steam_boundary: { experience: 1.0, gold: 1.15 },  // 蒸気の境界（炎・水）：ゴールド+15%
  twilight_realm: { experience: 1.1, gold: 1.1 },   // 黄昏の境界（光・闇）：両方+10%
  time_rift: { experience: 1.2, gold: 1.0 },        // 時空の狭間（未来）：経験値+20%
  primal_depths: { experience: 1.0, gold: 1.2 },    // 原始の深淵（原始）：ゴールド+20%
  unknown: { experience: 1.0, gold: 1.0 },          // 未知の領域：補正なし
};

/**
 * フロア種別ボーナス
 */
const FLOOR_TYPE_BONUSES = {
  normal: { experience: 1.0, gold: 1.0 },
  elite: { experience: 1.5, gold: 1.3 },
  treasure: { experience: 0.5, gold: 2.0 },
  event: { experience: 1.2, gold: 1.0 },
  rest: { experience: 0, gold: 0 },
  boss: { experience: 3.0, gold: 2.0 },
};

/**
 * 報酬計算クラス
 */
class RewardCalculator {
  /**
   * フロアクリア経験値を計算
   *
   * @param {Object} floor - フロア情報
   * @param {Object} context - コンテキスト（鍵、パーティ、大陸など）
   * @returns {number} 獲得経験値
   */
  calculateExperience(floor, context) {
    const baseExp = BASE_REWARDS.floor.experience;

    // 深度倍率
    const depthTier = Math.ceil((floor.depth || 1) / 10);
    const depthMultiplier = DEPTH_MULTIPLIERS[depthTier] || 1.0;

    // フロア種別倍率
    const floorType = floor.type || 'normal';
    const floorBonus = FLOOR_TYPE_BONUSES[floorType]?.experience || 1.0;

    // 大陸倍率
    const continent = context.continent || 'unknown';
    const continentBonus = CONTINENT_BONUSES[continent]?.experience || 1.0;

    // パーティレベル補正（高レベルだと経験値減少）
    const partyLevel = context.partyAverageLevel || 1;
    const levelPenalty = this._calculateLevelPenalty(floor.depth, partyLevel);

    // 運倍率（装備・パッシブから）
    const luckModifier = context.luckModifier || 1.0;

    const totalExp = Math.floor(
      baseExp * depthMultiplier * floorBonus * continentBonus * levelPenalty * luckModifier
    );

    return Math.max(1, totalExp);
  }

  /**
   * フロアクリアゴールドを計算
   *
   * @param {Object} floor - フロア情報
   * @param {Object} context - コンテキスト
   * @returns {number} 獲得ゴールド
   */
  calculateGold(floor, context) {
    const baseGold = BASE_REWARDS.floor.gold;

    // 深度倍率
    const depthTier = Math.ceil((floor.depth || 1) / 10);
    const depthMultiplier = DEPTH_MULTIPLIERS[depthTier] || 1.0;

    // フロア種別倍率
    const floorType = floor.type || 'normal';
    const floorBonus = FLOOR_TYPE_BONUSES[floorType]?.gold || 1.0;

    // 大陸倍率
    const continent = context.continent || 'unknown';
    const continentBonus = CONTINENT_BONUSES[continent]?.gold || 1.0;

    // ゴールドボーナス（装備・パッシブから）
    const goldModifier = context.goldModifier || 1.0;

    const totalGold = Math.floor(
      baseGold * depthMultiplier * floorBonus * continentBonus * goldModifier
    );

    return Math.max(1, totalGold);
  }

  /**
   * ボス撃破経験値を計算
   *
   * @param {Object} boss - ボス情報
   * @param {Object} context - コンテキスト
   * @returns {number} 獲得経験値
   */
  calculateBossExperience(boss, context) {
    const baseExp = BASE_REWARDS.boss.experience;

    // ボスレベルに応じた倍率
    const bossLevel = boss.level || 10;
    const levelMultiplier = 1 + (bossLevel - 10) * 0.05; // レベル10ごとに+50%

    // ボスタイプ倍率
    const bossTypeMultiplier = this._getBossTypeMultiplier(boss);

    // 大陸倍率
    const continent = context.continent || 'unknown';
    const continentBonus = CONTINENT_BONUSES[continent]?.experience || 1.0;

    // パーティレベル補正
    const partyLevel = context.partyAverageLevel || 1;
    const levelPenalty = this._calculateLevelPenalty(bossLevel, partyLevel);

    const totalExp = Math.floor(
      baseExp * levelMultiplier * bossTypeMultiplier * continentBonus * levelPenalty
    );

    return Math.max(50, totalExp);
  }

  /**
   * ボス撃破ゴールドを計算
   *
   * @param {Object} boss - ボス情報
   * @param {Object} context - コンテキスト
   * @returns {number} 獲得ゴールド
   */
  calculateBossGold(boss, context) {
    const baseGold = BASE_REWARDS.boss.gold;

    // ボスレベルに応じた倍率
    const bossLevel = boss.level || 10;
    const levelMultiplier = 1 + (bossLevel - 10) * 0.03;

    // ボスタイプ倍率
    const bossTypeMultiplier = this._getBossTypeMultiplier(boss);

    // 大陸倍率
    const continent = context.continent || 'unknown';
    const continentBonus = CONTINENT_BONUSES[continent]?.gold || 1.0;

    // ゴールドボーナス
    const goldModifier = context.goldModifier || 1.0;

    const totalGold = Math.floor(
      baseGold * levelMultiplier * bossTypeMultiplier * continentBonus * goldModifier
    );

    return Math.max(30, totalGold);
  }

  /**
   * 宝箱報酬を計算
   *
   * @param {Object} params - パラメータ
   * @param {string} params.continentId - 大陸ID
   * @param {number} params.floor - 現在階層
   * @param {number} params.maxFloor - 最大階層
   * @param {string} params.treasureType - 宝箱タイプ ('normal', 'rare', 'legendary')
   * @returns {{ gold: number, experience: number, bonus: Object }}
   */
  calculateTreasureReward(params) {
    const { continentId, floor, maxFloor, treasureType = 'normal' } = params;

    // 深度比率
    const depthRatio = floor / Math.max(1, maxFloor);

    // 宝箱タイプ別倍率
    const treasureMultipliers = {
      normal: 1.0,
      rare: 2.0,
      legendary: 4.0,
    };
    const treasureMultiplier = treasureMultipliers[treasureType] || 1.0;

    // 大陸倍率
    const continentBonus = CONTINENT_BONUSES[continentId]?.gold || 1.0;

    // 基本ゴールド計算
    const baseGold = 50 + Math.floor(depthRatio * 150);
    const totalGold = Math.floor(baseGold * treasureMultiplier * continentBonus);

    // 基本経験値計算
    const baseExp = 20 + Math.floor(depthRatio * 80);
    const expMultiplier = CONTINENT_BONUSES[continentId]?.experience || 1.0;
    const totalExp = Math.floor(baseExp * treasureMultiplier * expMultiplier);

    return {
      gold: totalGold,
      experience: totalExp,
      bonus: {
        treasureType,
        depthBonus: depthRatio > 0.5,
        continentBonus: continentBonus > 1.0,
      },
    };
  }

  /**
   * ダンジョンクリア報酬を計算
   *
   * @param {Object} dungeon - ダンジョン情報
   * @param {Object} context - コンテキスト
   * @returns {{ experience: number, gold: number, bonus: Object }}
   */
  calculateDungeonClearRewards(dungeon, context) {
    const baseExp = 1000;
    const baseGold = 500;

    // 階層数ボーナス
    const floorCount = dungeon.floors || 10;
    const floorBonus = 1 + (floorCount - 10) * 0.05;

    // 初クリアボーナス
    const firstClearBonus = context.isFirstClear ? 2.0 : 1.0;

    // 速度クリアボーナス（ターン数制限内）
    const speedBonus = this._calculateSpeedBonus(dungeon, context);

    // 無傷クリアボーナス
    const noDeathBonus = context.partyDeaths === 0 ? 1.3 : 1.0;

    const experience = Math.floor(
      baseExp * floorBonus * firstClearBonus * speedBonus * noDeathBonus
    );

    const gold = Math.floor(
      baseGold * floorBonus * firstClearBonus * speedBonus * noDeathBonus
    );

    return {
      experience,
      gold,
      bonus: {
        firstClear: context.isFirstClear,
        speedClear: speedBonus > 1.0,
        noDeathClear: context.partyDeaths === 0,
      },
    };
  }

  /**
   * レベル差によるペナルティを計算
   * @private
   */
  _calculateLevelPenalty(contentLevel, partyLevel) {
    const levelDiff = partyLevel - contentLevel;

    if (levelDiff <= 0) {
      // パーティレベルが低い場合はボーナス
      return Math.min(1.5, 1 + Math.abs(levelDiff) * 0.02);
    } else if (levelDiff <= 5) {
      return 1.0;
    } else if (levelDiff <= 10) {
      return 0.8;
    } else if (levelDiff <= 20) {
      return 0.5;
    } else {
      return 0.2; // 最低20%
    }
  }

  /**
   * ボスタイプ倍率を取得
   * @private
   */
  _getBossTypeMultiplier(boss) {
    if (boss.isTrueEnd) return 5.0;
    if (boss.isFinalBoss) return 3.0;
    if (boss.isForbiddenBoss) return 4.0;
    if (boss.isMirrorBoss) return 2.0;
    if (boss.isGuardian) return 2.5;
    if (boss.phases > 1) return 1.5 + (boss.phases - 1) * 0.3;
    return 1.0;
  }

  /**
   * 速度クリアボーナスを計算
   * @private
   */
  _calculateSpeedBonus(dungeon, context) {
    const targetTurns = (dungeon.floors || 10) * 5; // 1階あたり5ターン目安
    const actualTurns = context.totalTurns || targetTurns;

    if (actualTurns <= targetTurns * 0.5) {
      return 1.5; // 目標の50%以下で1.5倍
    } else if (actualTurns <= targetTurns * 0.75) {
      return 1.25;
    } else if (actualTurns <= targetTurns) {
      return 1.1;
    }

    return 1.0;
  }
}

export const rewardCalculator = new RewardCalculator();
export default RewardCalculator;

export {
  BASE_REWARDS,
  DEPTH_MULTIPLIERS,
  CONTINENT_BONUSES,
  FLOOR_TYPE_BONUSES,
};
